/**
 * Telehealth session calls: the call is started FROM the session and IS the
 * session (owner decisions, 2026-10-20).
 *
 * - Media by kind: phone-call sessions start a VOICE call; video / generic
 *   telehealth sessions start a VIDEO call (either can switch in-call).
 * - Who: the provider any time (not once cancelled); the member from
 *   {@link SESSION_CALL_MEMBER_LEAD_MS} before the scheduled start until the
 *   scheduled end (or while the session is still running). Nobody else —
 *   followers are never a party.
 * - Times: the call CONNECTING records the session start (`startedBy:
 *   'call'`); a deliberate hang-up records the end. A dropped call waits
 *   {@link SESSION_CALL_RECONNECT_WINDOW_MS} for a reconnect before the end
 *   is recorded (at the moment it dropped).
 *
 * Framework-agnostic: shared by the publish-voice-call-signal INVITE gate,
 * update-session-state and the web client.
 */

import { isCallbackSession } from './callback-session'
import { isFaceToFace, telehealthKind } from './telehealth'
import type { Session, SessionLifecycleState } from './types'

/** Members may start the session call this long before the scheduled start. */
export const SESSION_CALL_MEMBER_LEAD_MS = 5 * 60_000

/** A dropped session call that reconnects within this window keeps the session running. */
export const SESSION_CALL_RECONNECT_WINDOW_MS = 2 * 60_000

/** Client snackbar length for undoing a call-recorded start/end (server windows are longer). */
export const SESSION_CALL_UNDO_MS = 10_000

/** Upper bound on linked calls stored on one session row. */
export const MAX_SESSION_CALL_IDS = 20

export type SessionCallRole = 'provider' | 'member'
export type SessionCallMedia = 'voice' | 'video'

/** Session fields the call rules read (domain `Session` or a raw DynamoDB row). */
export type SessionCallSessionLike = Pick<
  Session,
  'start' | 'end' | 'lifecycleState' | 'providerEmail' | 'memberEmail'
> &
  Partial<Pick<Session, 'faceToFace' | 'nonFaceToFaceSessionType' | 'source' | 'callback' | 'coreStatus'>>

export type SessionCallBlock = 'face-to-face' | 'callback' | 'cancelled' | 'too-early' | 'ended'

export type SessionCallEligibility =
  | { ok: true }
  | { ok: false; reason: SessionCallBlock; /** Member window opening, for `too-early`. */ opensAt?: number }

const LIVE_STATES: ReadonlySet<SessionLifecycleState> = new Set<SessionLifecycleState>([
  'checked-in',
  'tracking',
  'checkout-reminded',
])

/** The session has a recorded start and no recorded end yet. */
export function isSessionLive(state: SessionLifecycleState): boolean {
  return LIVE_STATES.has(state)
}

/** VOICE for phone-call sessions, VIDEO for video / generic telehealth. */
export function sessionCallMediaType(
  session: { nonFaceToFaceSessionType?: string | null } | null | undefined,
): SessionCallMedia {
  return telehealthKind(session) === 'phone' ? 'voice' : 'video'
}

/** When the member may first start the session call (epoch ms). */
export function sessionCallMemberOpensAt(session: Pick<Session, 'start'>): number {
  return session.start - SESSION_CALL_MEMBER_LEAD_MS
}

/**
 * Whether `role` may start (or be rung for) the session call at `now`. Call
 * PERMISSIONS (directory reach, account calls toggle) are separate — this is
 * only the session rule.
 */
export function sessionCallEligibility(input: {
  role: SessionCallRole
  session: SessionCallSessionLike
  now: number
}): SessionCallEligibility {
  const { role, session, now } = input
  if (isFaceToFace(session)) return { ok: false, reason: 'face-to-face' }
  if (isCallbackSession({ source: session.source, callback: session.callback })) {
    return { ok: false, reason: 'callback' }
  }
  // Cancelled here (provider confirmed) or in AHP (booking cancelled).
  if (session.lifecycleState === 'cancelled' || session.coreStatus === 'cancelled') {
    return { ok: false, reason: 'cancelled' }
  }
  if (role === 'provider') return { ok: true }
  const opensAt = sessionCallMemberOpensAt(session)
  if (now < opensAt) return { ok: false, reason: 'too-early', opensAt }
  if (now > session.end && !isSessionLive(session.lifecycleState)) {
    return { ok: false, reason: 'ended' }
  }
  return { ok: true }
}

export type SessionCallInviteRejection = 'not-found' | 'not-a-party' | SessionCallBlock

function sameEmail(a: string | null | undefined, b: string | null | undefined): boolean {
  const x = a?.trim().toLowerCase()
  const y = b?.trim().toLowerCase()
  return Boolean(x) && x === y
}

/** Which session party dialled, when the two call parties are exactly the provider and member. */
export function sessionCallCallerRole(
  session: Pick<Session, 'providerEmail' | 'memberEmail'>,
  callerEmail: string | null | undefined,
  calleeEmail: string | null | undefined,
): SessionCallRole | null {
  if (sameEmail(callerEmail, session.providerEmail) && sameEmail(calleeEmail, session.memberEmail)) {
    return 'provider'
  }
  if (sameEmail(callerEmail, session.memberEmail) && sameEmail(calleeEmail, session.providerEmail)) {
    return 'member'
  }
  return null
}

/**
 * An UNLABELLED call (no `sessionUuid` — a redial from Recents / Call back)
 * may stand in for the session call only when it is between exactly the
 * session's provider and member AND it connected inside the session's call
 * window — judged at the CALL's own time, whichever party dialled: not before
 * the member window opened (or, for a session already started early, a
 * reconnect window before that start), and not after the session ended
 * unless it is still running.
 */
export function authorizeUnlabelledSessionCall(input: {
  session: SessionCallSessionLike & Partial<Pick<Session, 'actualStart' | 'actualEnd'>>
  callerEmail: string | null | undefined
  calleeEmail: string | null | undefined
  /** When the call connected (server time). */
  callAt: number
}): boolean {
  const { session, callAt } = input
  if (!sessionCallCallerRole(session, input.callerEmail, input.calleeEmail)) return false
  const rule = sessionCallEligibility({ role: 'provider', session, now: callAt })
  if (!rule.ok) return false
  const opens = Math.min(
    sessionCallMemberOpensAt(session),
    session.actualStart != null ? session.actualStart - SESSION_CALL_RECONNECT_WINDOW_MS : Number.POSITIVE_INFINITY,
  )
  if (callAt < opens) return false
  const closes = Math.max(session.end, session.actualEnd ?? Number.NEGATIVE_INFINITY)
  return callAt <= closes || isSessionLive(session.lifecycleState)
}

/**
 * Server gate for an INVITE carrying `sessionUuid`: the two call parties must
 * be exactly the session's provider and member (either direction), and the
 * caller must be inside their window.
 */
export function authorizeSessionCallInvite(input: {
  session: SessionCallSessionLike | null
  callerEmail: string | null | undefined
  calleeEmail: string | null | undefined
  now: number
}):
  | { ok: true; callerRole: SessionCallRole }
  | { ok: false; reason: SessionCallInviteRejection } {
  const { session, callerEmail, calleeEmail, now } = input
  if (!session) return { ok: false, reason: 'not-found' }
  const callerRole = sessionCallCallerRole(session, callerEmail, calleeEmail)
  if (!callerRole) return { ok: false, reason: 'not-a-party' }
  const eligibility = sessionCallEligibility({ role: callerRole, session, now })
  if (!eligibility.ok) return { ok: false, reason: eligibility.reason }
  return { ok: true, callerRole }
}

/** User-facing text for a rejected session-call INVITE (thrown by the signal lambda). */
export function sessionCallRejectionMessage(reason: SessionCallInviteRejection): string {
  switch (reason) {
    case 'not-found':
      return 'This session could not be found.'
    case 'not-a-party':
      return 'Only the session provider and member can join this session call.'
    case 'face-to-face':
      return 'Session calls are only for telehealth sessions.'
    case 'callback':
      return 'Callback bookings do not have a session call.'
    case 'cancelled':
      return 'This session was cancelled.'
    case 'too-early':
      return 'The session call opens 5 minutes before the session starts.'
    case 'ended':
      return 'This session has ended.'
  }
}

/** Client end cause for a `completed` end that was not a hang-up (peer vanished / meeting ended). */
export const PEER_LOST_END_CAUSE = 'peer-lost'

export type SessionCallEndPlan = 'record-now' | 'await-reconnect' | 'consent-missing'

/**
 * What to do when a CONNECTED session call ends:
 * - a deliberate hang-up (`completed`, either side) ends the session at once;
 * - the recording-consent window ending the call (owner decision) undoes a
 *   call-recorded start and records NO end — the provider records the times;
 * - anything else — a drop, an error, or a `completed` end whose cause is
 *   {@link PEER_LOST_END_CAUSE} (the peer's app/radio died, the meeting
 *   ended under us) — waits {@link SESSION_CALL_RECONNECT_WINDOW_MS} first.
 */
export function planSessionCallEnd(
  endReason: string | null | undefined,
  endCause?: string | null,
): SessionCallEndPlan {
  if (endReason === 'consent-missing') return 'consent-missing'
  if (endReason === 'completed' && endCause !== PEER_LOST_END_CAUSE) return 'record-now'
  return 'await-reconnect'
}

/** A plausible call id (the client mints UUIDs). */
export function isValidSessionCallId(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9._:-]{1,128}$/.test(value)
}

/** `existing` plus `callId`, de-duplicated and capped (newest kept). */
export function withSessionCallId(existing: readonly string[] | null | undefined, callId: string): string[] {
  const list = (existing ?? []).filter((id) => id !== callId)
  list.push(callId)
  return list.slice(-MAX_SESSION_CALL_IDS)
}
